export const formatCpfCnpj = (value: string | null | undefined): string => {
    if (!value) return '-';
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return value;
};

export const formatCurrency = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined || value === '') return '-';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

/**
 * Resolve o valor total da operação usando nullish coalescing para preservar 0 como valor legítimo.
 * Prioridade: total_amount → nominal_value → approved_value
 */
export const resolveTotalValue = (
    totalAmount: number | null | undefined,
    nominalValue: number | null | undefined,
    approvedValue: number | null | undefined,
): number | null => {
    return totalAmount ?? nominalValue ?? approvedValue ?? null;
};


export type TaxDocumentType = 'CPF' | 'CNPJ' | 'INVALID';

/**
 * Classifica um documento fiscal (CPF/CNPJ) pelo número de dígitos.
 * Retorna 'INVALID' para tamanhos que não correspondem a CPF (11) ou CNPJ (14).
 */
export const classifyTaxDocument = (value: string): TaxDocumentType => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) return 'CPF';
    if (digits.length === 14) return 'CNPJ';
    return 'INVALID';
};


const OPERATION_TO_CONTRACT_TYPE: Record<string, string> = {
    'RECEIVABLES_ASSIGNMENT': 'CESSAO_CREDITO',
    'STRUCTURED_OPERATION': 'CPR_ALIENACAO_FIDUCIARIA',
    'LOAN': 'CPR_F',
};

/**
 * Mapeia o operation_type (contracts-management) para o contract_type (contract service).
 * Retorna undefined se o tipo não for mapeável.
 */
export const mapOperationTypeToContractType = (operationType: string): string | undefined => {
    return OPERATION_TO_CONTRACT_TYPE[operationType];
};


/**
 * Normaliza o role extraído pela IA para o valor canônico usado pelo backend.
 * Resolve variações comuns (CESSIONARIO→CESSIONARIA, CESSIONALARIO→CESSIONARIA, etc.)
 */
const SIGNER_ROLE_ALIASES: Record<string, string> = {
    'CESSIONARIO': 'CESSIONARIA',
    'CESSIONALARIO': 'CESSIONARIA',
    'CESSIONALARIA': 'CESSIONARIA',
    'CREDOR_FIDUCIARIO': 'CREDORA_FIDUCIARIA',
    'DEVEDORA_FIDUCIARIA': 'DEVEDOR_FIDUCIARIO',
    'EMITENTE_CPR': 'EMITENTE',
    'FIADOR': 'AVALISTA',
    'GARANTIDOR': 'AVALISTA',
    'WITNESS': 'TESTEMUNHA',
    'ISSUER': 'EMITENTE',
    'ASSIGNOR': 'CEDENTE',
    'ASSIGNEE': 'CESSIONARIA',
};

const VALID_ROLES = [
    'EMITENTE', 'CEDENTE', 'CREDORA', 'CESSIONARIA',
    'CREDORA_FIDUCIARIA', 'DEVEDOR_FIDUCIARIO',
    'AVALISTA', 'AVALISTA_PF', 'AVALISTA_PJ',
    'INTERVENIENTE_ANUENTE', 'TESTEMUNHA', 'VALIDADOR',
];

export const normalizeSignerRole = (role: string): string => {
    const normalized = role.trim().toUpperCase().replace(/\s+/g, '_');
    if (VALID_ROLES.includes(normalized)) return normalized;
    if (SIGNER_ROLE_ALIASES[normalized]) return SIGNER_ROLE_ALIASES[normalized];
    return normalized; // fallback: return as-is (backend will handle)
};


const CONTRACT_STATUS_MAP: Record<string, string> = {
    'CREATED': 'Criado',
    'READY': 'Pronto para envio',
    'SENT_TO_SIGNATURE': 'Enviado para assinatura',
    'SIGNED': 'Assinado',
    'CANCELLED': 'Cancelado',
    // Signature process statuses
    'DRAFT': 'Rascunho',
    'SENT': 'Enviado',
    'PENDING': 'Pendente',
    'IN_PROGRESS': 'Em andamento',
    'COMPLETED': 'Concluído',
    'CLOSED': 'Encerrado',
    'REJECTED': 'Rejeitado',
    'EXPIRED': 'Expirado',
    'FROZEN': 'Congelado',
    'ERROR': 'Erro',
};

export const translateContractStatus = (status: string | undefined | null): string => {
    if (!status) return '-';
    return CONTRACT_STATUS_MAP[status] || status;
};
